import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import Loader from "../../components/common/Loader";
import {
  getMetaIntegration,
  getMetaOAuthUrl,
  getMetaAdAccounts,
  getCampaignMetrics,
  updateMetaAdAccount,
  MetaIntegration,
  MetaAdAccount,
  CampaignMetricsResponse,
} from "../../services/meta/metaService";
import { BarChart3, ArrowRight, Users, Eye, MousePointerClick, Video, RefreshCw, Megaphone } from "lucide-react";
import toast from "react-hot-toast";

const CampaignsPage = () => {
  const { language } = useAuth();

  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState<MetaIntegration | null>(null);
  const [adAccounts, setAdAccounts] = useState<MetaAdAccount[]>([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<CampaignMetricsResponse | null>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [datePreset, setDatePreset] = useState("last_7d");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadIntegration();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const loadIntegration = async () => {
    setLoading(true);
    try {
      const meta = await getMetaIntegration();
      setIntegration(meta);

      if (meta?.is_active) {
        const accounts = await getMetaAdAccounts();
        const accountList = accounts.adaccounts || [];
        setAdAccounts(accountList);

        const savedAccount = meta.ad_account_id;
        if (savedAccount && accountList.some((a) => a.id === savedAccount)) {
          setSelectedAdAccount(savedAccount);
          await loadMetrics(savedAccount, datePreset);
        } else if (accountList.length > 0) {
          const firstId = accountList[0].id;
          setSelectedAdAccount(firstId);
          await updateMetaAdAccount(firstId);
          await loadMetrics(firstId, datePreset);
        }
      }
    } catch {
      // No toast — user without Meta connected is expected, shows empty state
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async (adAccountId: string, preset: string) => {
    setLoadingMetrics(true);
    setMetrics(null);
    try {
      const data = await getCampaignMetrics(adAccountId, preset);
      setMetrics(data);
    } catch {
      toast.error(dictionary["campaigns-load-error"][language]);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleSelectAdAccount = async (adAccountId: string) => {
    if (adAccountId === selectedAdAccount) return;
    setSelectedAdAccount(adAccountId);
    setMetrics(null);
    try {
      await updateMetaAdAccount(adAccountId);
      await loadMetrics(adAccountId, datePreset);
    } catch {
      toast.error(dictionary["campaigns-load-error"][language]);
    }
  };

  const handleDatePresetChange = async (preset: string) => {
    setDatePreset(preset);
    if (selectedAdAccount) {
      await loadMetrics(selectedAdAccount, preset);
    }
  };

  const handleConnectMeta = async () => {
    setConnectError(null);
    setConnecting(true);
    try {
      const response = await getMetaOAuthUrl();
      const popup = window.open(
        response.authorizationUrl,
        "meta-oauth",
        "width=600,height=700,scrollbars=yes"
      );

      intervalRef.current = setInterval(async () => {
        if (popup?.closed) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          try {
            const meta = await getMetaIntegration();
            if (meta?.is_active) {
              setConnecting(false);
              toast.success(dictionary["integration-added"][language]);
              await loadIntegration();
              return;
            }
          } catch {
            // Integration not found
          }
          setConnecting(false);
          setConnectError(dictionary["meta-connect-error"][language]);
        }
      }, 500);
    } catch {
      setConnecting(false);
      setConnectError(dictionary["meta-connect-error"][language]);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  // Empty state: no Meta connected
  if (!integration?.is_active) {
    return (
      <div className="bg-[#F5F6FA] px-4 md:p-8 md:ml-4 min-h-screen">
        <h1 className="text-xl md:text-2xl font-bold mb-6">
          {dictionary["campaigns"][language]}
        </h1>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <BarChart3 className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            {dictionary["campaigns-empty-title"][language]}
          </h2>
          <p className="text-sm text-gray-500 mb-6 text-center max-w-md">
            {dictionary["campaigns-empty-description"][language]}
          </p>
          {connectError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 max-w-md w-full text-center">
              {connectError}
            </div>
          )}
          <button
            className="flex items-center gap-2 px-6 py-3 bg-[#0081FB] text-white rounded-lg hover:bg-[#006AD4] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConnectMeta}
            disabled={connecting}
          >
            {connecting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                {dictionary["campaigns-connect-meta"][language]}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  const selectedAccountName = adAccounts.find((a) => a.id === selectedAdAccount)?.name || selectedAdAccount;
  const hasActiveCampaigns = (metrics?.running_campaigns?.length ?? 0) > 0;
  const hasAnyMetrics = metrics && (metrics.reach > 0 || metrics.impressions > 0 || metrics.link_clicks > 0);

  // Main view: sidebar + metrics
  return (
    <div className="bg-[#F5F6FA] px-4 md:p-8 md:ml-4 min-h-screen">
      <h1 className="text-xl md:text-2xl font-bold mb-6">
        {dictionary["campaigns"][language]}
      </h1>

      <div className="flex gap-6">
        {/* Left sidebar: Ad accounts list */}
        {adAccounts.length > 0 && (
          <div className="w-64 flex-shrink-0">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">
              {dictionary["campaigns-ad-accounts"][language]}
            </h2>
            <div className="space-y-1">
              {adAccounts.map((account) => (
                <button
                  key={account.id}
                  className={`w-full p-3 rounded-lg text-left transition-all text-sm ${
                    selectedAdAccount === account.id
                      ? "bg-white border border-[#0081FB] shadow-sm"
                      : "bg-white border border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handleSelectAdAccount(account.id)}
                >
                  <p className={`font-medium truncate ${selectedAdAccount === account.id ? "text-[#0081FB]" : "text-gray-800"}`}>
                    {account.name}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{account.id}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Right: Metrics area */}
        <div className="flex-1 min-w-0">
          {/* Header with date selector and refresh */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-500">
              {selectedAccountName}
            </p>
            <div className="flex items-center gap-3">
              <select
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
                value={datePreset}
                onChange={(e) => handleDatePresetChange(e.target.value)}
              >
                <option value="last_7d">{dictionary["campaigns-last-7d"][language]}</option>
                <option value="last_14d">{dictionary["campaigns-last-14d"][language]}</option>
                <option value="last_30d">{dictionary["campaigns-last-30d"][language]}</option>
              </select>
              <button
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => selectedAdAccount && loadMetrics(selectedAdAccount, datePreset)}
                disabled={loadingMetrics}
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${loadingMetrics ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {/* Loading state */}
          {loadingMetrics ? (
            <div className="flex justify-center py-20">
              <Loader />
            </div>
          ) : metrics && (hasActiveCampaigns || hasAnyMetrics) ? (
            <>
              {/* Active campaigns */}
              {hasActiveCampaigns && (
                <div className="mb-6">
                  <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    {dictionary["campaigns-active"][language]} ({metrics.running_campaigns.length})
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {metrics.running_campaigns.map((name, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-full border border-green-200"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Metrics grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <MetricCard
                  icon={<Users className="w-5 h-5 text-blue-600" />}
                  label={dictionary["campaigns-metric-reach"][language]}
                  value={formatNumber(metrics.reach ?? 0)}
                />
                <MetricCard
                  icon={<Eye className="w-5 h-5 text-purple-600" />}
                  label={dictionary["campaigns-metric-impressions"][language]}
                  value={formatNumber(metrics.impressions ?? 0)}
                />
                <MetricCard
                  icon={<MousePointerClick className="w-5 h-5 text-orange-600" />}
                  label={dictionary["campaigns-metric-clicks"][language]}
                  value={formatNumber(metrics.link_clicks ?? 0)}
                />
                <MetricCard
                  icon={<Video className="w-5 h-5 text-teal-600" />}
                  label={dictionary["campaigns-metric-video-3s"][language]}
                  value={formatNumber(metrics.video_3s ?? 0)}
                />
                <MetricCard
                  icon={<Video className="w-5 h-5 text-indigo-600" />}
                  label={dictionary["campaigns-metric-video-50"][language]}
                  value={formatNumber(metrics.video_50 ?? 0)}
                />
                <MetricCard
                  icon={<BarChart3 className="w-5 h-5 text-gray-600" />}
                  label={dictionary["campaigns-metric-frequency"][language]}
                  value={(metrics.frequency ?? 0).toFixed(2)}
                />
              </div>

              {/* Active ads count */}
              <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                <p className="text-sm text-gray-500">
                  {dictionary["campaigns-active-ads"][language]}: <span className="font-semibold text-gray-800">{metrics.active_ads_count ?? 0}</span>
                </p>
              </div>
            </>
          ) : (
            /* Empty state: no active campaigns for this ad account */
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Megaphone className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                {dictionary["campaigns-no-active"][language]}
              </h2>
              <p className="text-sm text-gray-500 text-center max-w-md">
                {dictionary["campaigns-no-active-description"][language]}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="bg-white p-4 rounded-lg border border-gray-200">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-xs text-gray-500 font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
  </div>
);

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toLocaleString();
};

export default CampaignsPage;
